import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  Input,
  Select,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { MdInfoOutline, MdLibraryBooks, MdMarkEmailUnread } from "react-icons/md";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import ConfirmDialog from "@/components/consignado/Cobranca/components/ConfirmDialog";
import ComoUtilizarModal, {
  type PassoComoUtilizar,
} from "@/components/consignado/Cobranca/VinculosAtivosRedesign/ComoUtilizarModal";
import {
  CONCEITOS_TEMPLATE,
  PASSOS_EDITAR_TEMPLATE,
  PASSOS_NOVO_TEMPLATE,
} from "./comoUtilizar.content";
import {
  CANAL_LABEL,
  CONTEXTO_LABEL,
  CanalComunicacao,
  CatalogoDisparo,
  ContextoDisparo,
  CriarDisparoTemplateDto,
  DisparoTemplateItem,
  PROVEDOR_LABEL,
  PropriedadeCatalogo,
  ProvedorComunicacao,
  apelidoDaPropriedade,
  apelidoDisponivel,
  tokenVariavel,
} from "@/models/consignado/disparo.model";
import { limparVariaveisOrfas, novaVariavel, novoCanal, semVariaveis } from "./disparo-draft";
import {
  TemplateDraft,
  TemplateErros,
  draftDeTemplate,
  draftParaTemplateDto,
  semErrosTemplate,
  templateDraftVazio,
  validarTemplateDraft,
} from "./template-draft";
import {
  canaisDisponiveis as canaisDoCatalogo,
  modosDoProvedor,
  formatoPadrao,
  provedoresDoCanal,
} from "./catalogo.helpers";
import { SLIDE_FORMATOS } from "./FormatosComoUtilizar";
import SectionCard from "./SectionCard";
import CampoLabel from "./CampoLabel";
import TemplateMensagemForm from "./TemplateMensagemForm";
import VariaveisSidebar from "./VariaveisSidebar";
import { useSaidaProtegida } from "./useSaidaProtegida";
import { VariavelInsertContext, type InsertFn } from "./VariavelInsertContext";
import { fieldFont, labelStyles } from "./styleTokens";

export type TemplateScreenMode = "create" | "edit";

type Props = {
  mode: TemplateScreenMode;
  template: DisparoTemplateItem | null;
  catalogo?: CatalogoDisparo;
  isSaving?: boolean;
  onBack: () => void;
  /**
   * Devolvendo a promessa do POST/PUT, o Salvar fica travado até ela
   * terminar — é o que impede o duplo clique de gravar dois templates.
   */
  onSave: (dto: CriarDisparoTemplateDto) => void | Promise<unknown>;
  /** Aberto por cima do formulário de disparo (TemplateEditorJanela). */
  emJanela?: boolean;
};

const SUBTITLES: Record<TemplateScreenMode, string> = {
  create: "Novo template",
  edit: "Editar template",
};

const PASSOS_POR_MODO: Record<TemplateScreenMode, PassoComoUtilizar[]> = {
  create: PASSOS_NOVO_TEMPLATE,
  edit: PASSOS_EDITAR_TEMPLATE,
};

/**
 * Cadastro de um template reutilizável.
 *
 * O texto é o mesmo de um canal de disparo, então o editor é o mesmo
 * componente (TemplateMensagemForm) — o que muda é o cabeçalho: aqui se
 * declara para qual contexto, canal e provedor o template serve, e é essa
 * tripla que o disparo terá de bater na hora de usá-lo.
 *
 * Remetente e origem de contato não aparecem: pertencem ao disparo, não ao
 * texto guardado.
 */
export default function TemplateScreen({
  mode,
  template,
  catalogo,
  isSaving,
  onBack,
  onSave,
  emJanela = false,
}: Props) {
  const backgroundStyle = useConditionalBackground();
  const [draft, setDraft] = useState<TemplateDraft>(() =>
    mode === "edit" && template ? draftDeTemplate(template) : templateDraftVazio(),
  );
  const [erros, setErros] = useState<TemplateErros>({});

  const [activeInsert, setActiveInsert] = useState<InsertFn | null>(null);
  const registerActiveInsert = useCallback(
    (_canalId: string, insert: InsertFn) => setActiveInsert(() => insert),
    [],
  );

  // Sair com alteração não salva pede confirmação — mesmo comportamento do
  // formulário de disparo (ver useSaidaProtegida).
  const saida = useSaidaProtegida({ draft, ativo: true, onBack, historico: !emJanela });

  useEffect(() => {
    setErros({});
    saida.zerarReferencia();
    setDraft(mode === "edit" && template ? draftDeTemplate(template) : templateDraftVazio());
  }, [mode, template]);

  const provedores = provedoresDoCanal(catalogo, draft.mensagem.canal);
  const canaisPossiveis = canaisDoCatalogo(catalogo);

  // Mesma limpeza do disparo: apagar `{{apelido}}` do texto — ou remover a
  // linha de parâmetro que o tinha — descarta a variável, que sai do bloco de
  // variáveis e deixa de aparecer como usada na sidebar.
  const handleMensagem = (patch: Partial<TemplateDraft["mensagem"]>) =>
    setDraft((prev) => ({ ...prev, mensagem: limparVariaveisOrfas({ ...prev.mensagem, ...patch }) }));

  // Como no disparo: os campos são de cada contexto, então a troca tira as
  // variáveis da mensagem (declaração e `{{token}}`) — ver semVariaveis.
  const handleContexto = (contexto: ContextoDisparo) =>
    setDraft((prev) =>
      prev.contexto === contexto ? prev : { ...prev, contexto, mensagem: semVariaveis(prev.mensagem) },
    );

  // Trocar o canal invalida provedor e mensagem, como no cadastro do disparo.
  const handleCanal = (canal: CanalComunicacao) => {
    const provedoresNovos = provedoresDoCanal(catalogo, canal);
    const provedor = provedoresNovos.length === 1 ? provedoresNovos[0] : null;
    const modos = provedor ? modosDoProvedor(catalogo, canal, provedor) : [];
    setDraft((prev) => ({
      ...prev,
      mensagem: {
        ...novoCanal(canal),
        id: prev.mensagem.id,
        provedor,
        ...(modos.length ? { modo: modos[0] } : {}),
      },
    }));
  };

  const handleProvedor = (provedor: ProvedorComunicacao | "") => {
    if (!provedor) {
      handleMensagem({ provedor: null });
      return;
    }
    const modos = modosDoProvedor(catalogo, draft.mensagem.canal, provedor);
    handleMensagem({
      provedor,
      ...(modos.length && !modos.includes(draft.mensagem.modo) ? { modo: modos[0] } : {}),
    });
  };

  /** Mesmo comportamento do disparo: o clique na sidebar declara e insere. */
  const handleSelecionarVariavel = (p: PropriedadeCatalogo) => {
    if (!activeInsert) return;
    const existente = draft.mensagem.variaveis.find(
      (v) => v.origem === "PROPRIEDADE" && v.propriedade === p.id,
    );
    if (existente) {
      activeInsert(tokenVariavel(existente.apelido));
      return;
    }
    const apelido = apelidoDisponivel(
      apelidoDaPropriedade(p.id),
      draft.mensagem.variaveis.map((v) => v.apelido),
    );
    const variavel = novaVariavel(apelido, p.id, formatoPadrao(p));
    // Fora do handleMensagem: a limpeza descartaria a variável antes de o
    // token chegar ao texto. As duas escritas são funcionais e compõem no
    // mesmo tick — a do texto, que vem do insert, já limpa com o token lá.
    setDraft((prev) => ({
      ...prev,
      mensagem: { ...prev.mensagem, variaveis: [...prev.mensagem.variaveis, variavel] },
    }));
    activeInsert(tokenVariavel(apelido));
  };

  // Trava do Salvar. O `isSaving` só chega no render seguinte ao primeiro
  // clique, e um segundo clique antes disso gravava dois templates iguais.
  // A ref trava na hora (mesma trava do DisparoScreen).
  const salvando = useRef(false);
  // Editar muda a mensagem de todos os disparos vinculados, inclusive dos
  // ativos, no próximo envio — por isso a edição pede confirmação.
  const [confirmarEdicao, setConfirmarEdicao] = useState(false);

  const enviar = () => {
    if (salvando.current) return;
    salvando.current = true;
    Promise.resolve(onSave(draftParaTemplateDto(draft))).finally(() => {
      salvando.current = false;
    });
  };

  const handleSubmit = () => {
    if (salvando.current) return;
    const validacao = validarTemplateDraft(draft, catalogo);
    setErros(validacao);
    if (!semErrosTemplate(validacao)) return;
    if (mode === "edit") {
      setConfirmarEdicao(true);
      return;
    }
    enviar();
  };

  return (
    <VariavelInsertContext.Provider value={{ registerActiveInsert }}>
      <Box w="100%" {...saida.capturas}>
        <Box px={{ base: "2", md: "4" }} pt={{ base: "2", md: "4" }}>
          <Box
            {...backgroundStyle}
            borderRadius="md"
            p={{ base: "4", md: "6" }}
            color="white"
            boxShadow="md"
            width="100%"
          >
            <Flex align="center" justify="space-between">
              <Flex align="center">
                <IconButton
                  aria-label="Voltar"
                  icon={<ArrowBackIcon />}
                  variant="ghost"
                  color="white"
                  _hover={{ bg: "whiteAlpha.300" }}
                  onClick={saida.pedirSaida}
                  mr={3}
                />
                <MdLibraryBooks size="2rem" />
                <Box ml={3}>
                  <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" fontFamily="Lexend Deca">
                    Templates de mensagem
                  </Text>
                  <Text fontSize="sm" opacity={0.85} fontFamily="Lexend Deca">
                    {SUBTITLES[mode]}
                  </Text>
                </Box>
              </Flex>

              <ComoUtilizarModal
                tituloTela={SUBTITLES[mode]}
                passos={PASSOS_POR_MODO[mode]}
                conceitos={CONCEITOS_TEMPLATE}
                slidesExtras={[SLIDE_FORMATOS]}
                maxColunas={2}
              />
            </Flex>
          </Box>
        </Box>

        {/* Abaixo de lg a sidebar desce para baixo do formulário (ver VariaveisSidebar). */}
        <Flex w="100%" align="stretch" direction={{ base: "column", lg: "row" }}>
          <Box flex={1} minW={0} px={{ base: "2", md: "4" }} pb={{ base: "2", md: "4" }} pt={6}>
            <Box
              w="100%"
              maxW="1200px"
              mx="auto"
              bg="white"
              borderRadius="xl"
              boxShadow="sm"
              p={{ base: 4, md: 6 }}
            >
              <VStack spacing={5} align="stretch">
                <SectionCard title="Identificação do template" icon={MdInfoOutline}>
                  <VStack spacing={5} align="stretch">
                    <FormControl isInvalid={!!erros.nome} isRequired>
                      <CampoLabel ajuda="Como o template aparece na listagem.">Nome do template</CampoLabel>
                      <Input
                        {...fieldFont}
                        placeholder="Ex.: E-mail de pagamento ao tomador"
                        value={draft.nome}
                        onChange={(e) => setDraft((prev) => ({ ...prev, nome: e.target.value }))}
                      />
                      <FormErrorMessage {...fieldFont}>{erros.nome}</FormErrorMessage>
                    </FormControl>

                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
                      <GridItem>
                        <FormControl isRequired>
                          <CampoLabel ajuda="A entidade de onde saem as variáveis deste template.">Contexto</CampoLabel>
                          <Select
                            {...fieldFont}
                            value={draft.contexto}
                            onChange={(e) => handleContexto(e.target.value as ContextoDisparo)}
                          >
                            {(catalogo?.entidades ?? []).map((e) => (
                              <option key={e.id} value={e.id}>
                                {CONTEXTO_LABEL[e.id] ?? e.descricao}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl isRequired>
                          <CampoLabel ajuda="Por onde a mensagem sai.">Canal</CampoLabel>
                          <Select
                            {...fieldFont}
                            value={draft.mensagem.canal}
                            onChange={(e) => handleCanal(e.target.value as CanalComunicacao)}
                          >
                            {canaisPossiveis.map((c) => (
                              <option key={c} value={c}>
                                {CANAL_LABEL[c]}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl isRequired>
                          <CampoLabel ajuda="Quem entrega a mensagem.">Provedor</CampoLabel>
                          <Select
                            {...fieldFont}
                            placeholder="Selecione"
                            value={draft.mensagem.provedor ?? ""}
                            onChange={(e) =>
                              handleProvedor(e.target.value as ProvedorComunicacao | "")
                            }
                          >
                            {provedores.map((p) => (
                              <option key={p} value={p}>
                                {PROVEDOR_LABEL[p]}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    <Text fontSize="xs" color="gray.500" {...fieldFont}>
                      Esta tripla é o que amarra o template: um disparo só pode usá-lo se tiver o
                      mesmo contexto, canal e provedor.
                    </Text>

                    <FormControl display="flex" alignItems="center" gap={3}>
                      <Switch
                        colorScheme="green"
                        isChecked={draft.ativo}
                        onChange={(e) => setDraft((prev) => ({ ...prev, ativo: e.target.checked }))}
                      />
                      <Box>
                        <FormLabel {...labelStyles} mb={0}>
                          Ativo
                        </FormLabel>
                        <Text fontSize="xs" color="gray.500" {...fieldFont}>
                          Template inativo não pode ser escolhido por um disparo.
                        </Text>
                      </Box>
                    </FormControl>
                  </VStack>
                </SectionCard>

                <SectionCard title="Mensagem" icon={MdMarkEmailUnread}>
                  {erros.mensagem && (
                    <Flex align="center" gap={1.5} bg="red.50" borderRadius="md" px={3} py={2} mb={3}>
                      <WarningTwoIcon boxSize={3} color="red.500" flexShrink={0} />
                      <Text fontSize="xs" color="red.600" {...fieldFont}>
                        {erros.mensagem}
                      </Text>
                    </Flex>
                  )}
                  <TemplateMensagemForm
                    canal={draft.mensagem}
                    catalogo={catalogo}
                    contexto={draft.contexto}
                    onChange={handleMensagem}
                    onInserirToken={(token) => activeInsert?.(token)}
                  />
                </SectionCard>
              </VStack>

              <Flex justify="flex-end" gap={3} mt={8} pt={4} borderTopWidth="1px" borderColor="gray.100">
                <Button variant="ghost" onClick={saida.pedirSaida} {...fieldFont}>
                  Cancelar
                </Button>
                <Button colorScheme="green" onClick={handleSubmit} isLoading={isSaving} {...fieldFont}>
                  Salvar
                </Button>
              </Flex>
            </Box>
          </Box>

          <VariaveisSidebar
            contexto={draft.contexto}
            catalogo={catalogo}
            // Não há seção de filtro aqui: template não filtra nada, só
            // guarda a mensagem. A sidebar fica sempre na sessão de variáveis.
            disparoAberto={false}
            hasActiveTarget={!!activeInsert}
            onSelectVariavel={handleSelecionarVariavel}
            variaveisUsadas={draft.mensagem.variaveis
              .filter((v) => v.origem === "PROPRIEDADE")
              .map((v) => v.propriedade)}
          />
        </Flex>

        <ConfirmDialog
          {...saida.dialogo}
          titulo="Sair sem salvar?"
          descricao="As alterações deste template ainda não foram salvas e serão perdidas."
          confirmLabel="Sair sem salvar"
          cancelLabel="Continuar editando"
        />

        <ConfirmDialog
          isOpen={confirmarEdicao}
          onClose={() => setConfirmarEdicao(false)}
          onConfirm={() => {
            setConfirmarEdicao(false);
            enviar();
          }}
          titulo="Salvar as alterações do template?"
          descricao="Os disparos que usam este template seguem o template: a mensagem deles muda junto, inclusive a dos disparos ativos, a partir do próximo envio."
          confirmLabel="Salvar"
          cancelLabel="Continuar editando"
          colorScheme="green"
        />
      </Box>
    </VariavelInsertContext.Provider>
  );
}
